import { Button, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import type { GetRevenueChartDataResponse } from '@weft/api-definition';
import { getEmptyMonthlyRevenue } from '@weft/services';
import { DEFAULT_CURRENCY } from '@weft/types';
import { extractValues, sum, values } from '@weft/utils';
import { ColorDot } from 'apps/app/src/components/design/ColorDot';
import { Price } from 'apps/app/src/components/design/Price';
import { withSuspenseBoundary } from 'apps/app/src/components/utility/SuspenseBoundary';
import { useGetRevenueChartData } from 'apps/app/src/hooks/requests/use-get-revenue-chart-data';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { DataDashboardLoadingSkeleton } from '../DataDashboardLoadingSkeleton';
import { RevenueBarChart } from './revenue-bar-chart';

const RevenueSection: React.FC = () => {
  const vyTheme = useTheme();
  const { data: { revenueChartData } } = useGetRevenueChartData();
  const year = dayjs().year();

  const mrrValue = useThisMonthRevenue(revenueChartData);
  const usageValues = extractValues(revenueChartData.usageRevenuePerMonth, 'scheduledCharge');
  const fixedValues = extractValues(revenueChartData.fixedRevenuePerMonth, 'scheduledCharge');
  const revenueSum = useMemo(() => sum(...usageValues, ...fixedValues), [usageValues, fixedValues]);

  return (
    <Paper
      variant='outlined' 
      sx={(theme) => ({
        boxShadow: theme.shadows[1],
        padding: 3,
      })}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        width="100%"
      >
        <Typography variant='h6'>Revenue</Typography>
        <Button
          component={NavLink}
          to="/reports?reportType=Master+revenue+report"
          sx={{ marginLeft: 'auto' }}
          variant='outlined'
          size='small'
        >
          See Report
        </Button>
      </Stack>

      <Stack
        marginTop={2}
        marginLeft={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Stack
          direction="row"
          divider={
            <Divider
              orientation='vertical'
              flexItem
            />}
          gap={3}
        >
          <Stack
            gap={1}
          >
            <Typography
              variant='body2'
              sx={{ color: vyTheme.palette.text.secondary }}
            >
              {`YTD ${year}`}
            </Typography>
            <Price
              rounded
              variant='h5'
              currency={DEFAULT_CURRENCY}
            >
              {revenueSum}
            </Price>
          </Stack>
          <Stack
            gap={1}
          >
            <Typography
              variant='body2'
              sx={{ color: vyTheme.palette.text.secondary }}
            >
              MRR
            </Typography>
            <Price
              rounded
              variant='h5'
              currency={DEFAULT_CURRENCY}
            >
              {mrrValue.scheduledCharge}
            </Price>
          </Stack>
          <Stack
            gap={1}
          >
            <Typography
              variant='body2'
              sx={{ color: vyTheme.palette.text.secondary }}
            >
              ARR
            </Typography>
            <Price
              rounded
              variant='h5'
              currency={DEFAULT_CURRENCY}
            >
              {mrrValue.scheduledCharge * 12}
            </Price>
          </Stack>
        </Stack>
        <Stack
          gap={1}
          alignItems="center"
          direction="row"
        >
          <ColorDot color={vyTheme.palette.secondary.main}/>
          <Typography
            color={vyTheme.palette.text.secondary}
            variant='body2'
          >
            Forecast
          </Typography>
        </Stack>
      </Stack>
      <RevenueBarChart
        deferredStartDate={new Date()}
        revenueChartData={revenueChartData}
      />
    </Paper>
  );
};

function useThisMonthRevenue(revenueChartData: GetRevenueChartDataResponse['revenueChartData']) {
  const currentMonthIndex = dayjs().month();

  return useMemo(() => {
    const thisMonthRevenue = values(revenueChartData.deferredRevenuePerMonth)[currentMonthIndex];

    return thisMonthRevenue ?? getEmptyMonthlyRevenue();
  }, [currentMonthIndex, revenueChartData.deferredRevenuePerMonth]);
}

const RevenueSectionWithSuspense = withSuspenseBoundary(RevenueSection, { suspenseProps: { fallback: <DataDashboardLoadingSkeleton/> } });

export { RevenueSectionWithSuspense as RevenueSection };